package com.devsusana.portfolio.components.sections

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P

/**
 * Section featuring recent engineering articles and blog posts.
 */
@Composable
fun BlogSection(modifier: Modifier = Modifier) {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .id("blog")
            .styleModifier { property("scroll-margin-top", "5rem") }
            .padding(topBottom = 3.cssRem),
        verticalArrangement = Arrangement.spacedBy(1.5.cssRem)
    ) {
        // Section Header
        Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SpanText(
                text = strings.blogEyebrow,
                modifier = Modifier
                    .color(SitePalette.androidGreen)
                    .fontSize(0.9.cssRem)
                    .fontWeight(FontWeight.Bold)
            )

            H2 {
                SpanText(
                    text = strings.blogTitle,
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .styleModifier { property("font-size", "clamp(1.4rem, 4.5vw, 2rem)") }
                        .fontWeight(FontWeight.Bold)
                )
            }

            P {
                SpanText(
                    text = strings.blogSubtitle,
                    modifier = Modifier
                        .color(SitePalette.textSecondary(colorMode))
                        .fontSize(1.1.cssRem)
                )
            }
        }

        // Post Cards
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            strings.blogPosts.forEach { post ->
                Link(
                    path = "/posts/${post.slug}",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(SitePalette.surface(colorMode))
                            .borderRadius(12.px)
                            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("padding", "clamp(1rem, 2.5vw, 1.5rem)")
                                property("transition", "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease")
                            }
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .minHeight(120.px),
                            verticalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(verticalArrangement = Arrangement.spacedBy(0.6.cssRem)) {
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .styleModifier {
                                            property("flex-wrap", "wrap")
                                            property("justify-content", "space-between")
                                            property("align-items", "flex-start")
                                            property("gap", "0.5rem")
                                        }
                                ) {
                                    H3(
                                        attrs = {
                                            style {
                                                property("margin", "0")
                                                property("flex", "1 1 240px")
                                            }
                                        }
                                    ) {
                                        SpanText(
                                            text = post.title,
                                            modifier = Modifier
                                                .color(SitePalette.textPrimary(colorMode))
                                                .fontWeight(FontWeight.Bold)
                                                .fontSize(1.2.cssRem)
                                        )
                                    }
                                    SpanText(
                                        text = "${post.date} • ${post.readingTime}",
                                        modifier = Modifier
                                            .color(SitePalette.textSecondary(colorMode))
                                            .fontSize(0.85.cssRem)
                                            .styleModifier {
                                                property("flex-shrink", "0")
                                                property("white-space", "nowrap")
                                            }
                                    )
                                }

                                SpanText(
                                    text = post.description,
                                    modifier = Modifier
                                        .color(SitePalette.textSecondary(colorMode))
                                        .lineHeight(1.5.cssRem)
                                )
                            }

                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = 1.cssRem)
                                    .styleModifier {
                                        property("flex-wrap", "wrap")
                                        property("justify-content", "space-between")
                                        property("align-items", "center")
                                        property("gap", "0.75rem")
                                    }
                            ) {
                                Row(
                                    modifier = Modifier
                                        .styleModifier {
                                            property("flex-wrap", "wrap")
                                            property("gap", "0.5rem")
                                            property("flex", "1 1 auto")
                                        }
                                ) {
                                    post.tags.forEach { tag ->
                                        TechBadge(tag)
                                    }
                                }

                                Box(
                                    modifier = Modifier
                                        .styleModifier {
                                            property("flex-shrink", "0")
                                            property("white-space", "nowrap")
                                        }
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(0.4.cssRem),
                                        modifier = Modifier
                                            .borderRadius(8.px)
                                            .backgroundColor(
                                                if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.1f)
                                                else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(4, 120, 87, 0.08f)
                                            )
                                            .padding(leftRight = 0.85.cssRem, topBottom = 0.45.cssRem)
                                            .border(
                                                1.px,
                                                org.jetbrains.compose.web.css.LineStyle.Solid,
                                                if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.25f)
                                                else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(4, 120, 87, 0.2f)
                                            )
                                    ) {
                                        SpanText(
                                            text = strings.blogReadArticle,
                                            modifier = Modifier
                                                .color(if (colorMode.isDark) SitePalette.androidGreen else com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x04, 0x78, 0x57))
                                                .fontSize(0.9.cssRem)
                                                .fontWeight(FontWeight.Bold)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
